/**
 * TUVI - API Service
 * Capa de compatibilidad: mismas firmas, ahora usa GraphQL internamente
 */

import axios from 'axios';
import client from './graphqlClient';

// Mantener axios para compatibilidad con componentes que lo usan directamente
const isProduction = window.location.hostname !== 'localhost';

const API_URL = isProduction
  ? 'https://tuvisistema.com/api'
  : 'http://localhost/TUVI/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      return Promise.reject(error.response.data);
    } else if (error.request) {
      return Promise.reject({ message: 'No se pudo conectar con el servidor' });
    }
    return Promise.reject({ message: error.message });
  }
);

// ==========================================
// PROYECTOS — Ahora via GraphQL
// ==========================================

export const crearProyecto = async (data) => {
  const result = await client.mutate(`
    mutation CrearProyecto($input: CrearProyectoInput!) {
      crearProyecto(input: $input) {
        success message proyecto_id capitulos_creados actividades_creadas
      }
    }
  `, {
    input: {
      proyecto: data.proyecto,
      capitulos: data.capitulos?.map(c => ({
        numero: c.numero,
        nombre: c.nombre,
        subtotal: c.subtotal || 0,
        porcentaje: c.porcentaje || 0,
        actividades: c.actividades?.map(a => ({
          descripcion: a.descripcion,
          valor: a.valor || 0,
          orden: a.orden || 0
        }))
      }))
    }
  });
  return { data: result.crearProyecto };
};

export const obtenerProyecto = async (id) => {
  // Mantener compatibilidad con PHP (api/proyectos.php?id=X)
  return api.get(`/proyectos.php?id=${id}`);
};

export const obtenerProyectos = async (estado = null) => {
  const url = estado ? `/proyectos.php?estado=${estado}` : '/proyectos.php';
  return api.get(url);
};

export const actualizarEstadoProyecto = async (id, estado) => {
  return api.put(`/proyectos.php?id=${id}`, { estado });
};

export const actualizarProyecto = async (id, data) => {
  return api.put(`/proyectos.php?id=${id}`, data);
};

export const eliminarProyecto = async (id) => {
  return api.delete(`/proyectos.php?id=${id}`);
};

export const verificarProyectoExistente = async (datos) => {
  const data = await client.query(`
    query VerificarProyecto($nombre_cliente: String!, $proyecto_nombre: String!, $apartamento: String) {
      verificarProyecto(nombre_cliente: $nombre_cliente, proyecto_nombre: $proyecto_nombre, apartamento: $apartamento) {
        existe total proyectos { id nombre_cliente proyecto_nombre apartamento valor_final estado }
      }
    }
  `, {
    nombre_cliente: datos.nombre_cliente || '',
    proyecto_nombre: datos.proyecto_nombre || '',
    apartamento: datos.apartamento || null
  });
  return { data: data.verificarProyecto };
};

export const actualizarProyectoActividades = async (proyectoId, datos) => {
  const result = await client.mutate(`
    mutation AgregarActividades($proyecto_id: Int!, $capitulos: [CapituloActividadesInput!]!) {
      agregarActividades(proyecto_id: $proyecto_id, capitulos: $capitulos) {
        success message actividades_agregadas actividades_existentes capitulos_actualizados valor_agregado
      }
    }
  `, {
    proyecto_id: proyectoId,
    capitulos: datos.capitulos
  });
  return { data: result.agregarActividades };
};

export const reiniciarProyecto = async (proyectoId, datos) => {
  const result = await client.mutate(`
    mutation ReiniciarProyecto($proyecto_id: Int!, $proyecto: ActualizarProyectoBasicoInput, $capitulos: [CapituloActividadesInput!]!) {
      reiniciarProyecto(proyecto_id: $proyecto_id, proyecto: $proyecto, capitulos: $capitulos) {
        success message capitulos_eliminados actividades_eliminadas capitulos_creados actividades_creadas
      }
    }
  `, {
    proyecto_id: proyectoId,
    proyecto: datos.proyecto || null,
    capitulos: datos.capitulos
  });
  return { data: result.reiniciarProyecto };
};

// ==========================================
// PROVEEDORES
// ==========================================

export const obtenerProveedores = () => api.get('/proveedores.php');
export const crearProveedor = (data) => api.post('/proveedores.php', data);
export const actualizarProveedor = (id, data) => api.put(`/proveedores.php?id=${id}`, data);
export const eliminarProveedor = (id) => api.delete(`/proveedores.php?id=${id}`);

// ==========================================
// FACTURAS
// ==========================================

export const crearFactura = (data) => api.post('/facturas.php', data);
export const obtenerFacturasPorProveedor = (proveedorId) => api.get(`/facturas.php?proveedor_id=${proveedorId}`);
export const obtenerFacturasPorProyecto = (proyectoId) => api.get(`/facturas.php?proyecto_id=${proyectoId}`);

// ==========================================
// PAGOS
// ==========================================

export const registrarPago = (data) => api.post('/pagos.php', data);
export const obtenerPagosPorFactura = (facturaId) => api.get(`/pagos.php?factura_id=${facturaId}`);

// ==========================================
// BODEGA
// ==========================================

export const registrarMovimientoBodega = (data) => api.post('/bodega.php', data);
export const obtenerMovimientosBodega = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return api.get(`/bodega.php?${params}`);
};

export default api;
